/**
 * Lyric Polish Service
 * Core engine: 4 reviewers grade & suggest improvements in parallel,
 * synthesizer merges the best improvements into a polished version.
 * Duplicates the proven callProvider pattern from lyricOrchestratorService.ts.
 */

import { generateText as geminiGenerateText } from '@/lib/gemini';
import { generateText as anthropicGenerateText } from '@/lib/anthropic';
import { trackUsage } from '@/utils/costTracker';
import {
  type LLMProvider,
  getProvider,
  getGeminiKey, getGeminiModel,
  getClaudeKey, getClaudeProxy, getClaudeModel,
  getOpenAIKey, getOpenAIModel,
  getXAIKey, getXAIModel,
} from './llmShared';
import { openaiCompatFetch } from '../utils/openaiProxyFetch';
import {
  LYRIC_WRITER_AGENTS,
  POLISH_AGENT_PROMPTS,
  POLISH_SYNTHESIZER_SYSTEM_PROMPT,
  DEFAULT_POLISH_DELIBERATION_CONFIG,
} from '@/data/lyricOrchestratorAgents';
import type {
  LyricAgent,
  LyricDraftScores,
  LyricDeliberationConfig,
  LyricArchitectureContext,
  PolishReview,
  PolishSynthesis,
  PolishResult,
  PolishProgressEvent,
  SectionSuggestion,
  SectionDecision,
  RawPolishReviewResponse,
  RawPolishSynthesisResponse,
} from '@/types/lyricOrchestrator.types';
import type { ParsedSunoOutput, HookhouseFormData } from '@/types';
import { SUNO_LYRICS_INJECTION_LINES } from '@/shared/sunoLyricsInjection';

// =============================================================================
// PROVIDER-AGNOSTIC LLM CALLER (duplicated from lyricOrchestratorService.ts)
// =============================================================================

interface LLMCallResult {
  text: string;
  inputTokens: number;
  outputTokens: number;
  provider: string;
  model: string;
}

async function callProvider(
  systemPrompt: string,
  userPrompt: string,
  provider?: LLMProvider,
  signal?: AbortSignal
): Promise<LLMCallResult> {
  const activeProvider = provider || getProvider();

  switch (activeProvider) {
    case 'gemini': return callGemini(systemPrompt, userPrompt, signal);
    case 'claude': return callClaude(systemPrompt, userPrompt, signal);
    case 'openai': return callOpenAI(systemPrompt, userPrompt, 'https://api.openai.com/v1/chat/completions', getOpenAIKey(), getOpenAIModel(), signal);
    case 'xai': return callOpenAI(systemPrompt, userPrompt, 'https://api.x.ai/v1/chat/completions', getXAIKey(), getXAIModel(), signal);
    default: throw new Error(`Unknown provider: ${activeProvider}`);
  }
}

async function callGemini(systemPrompt: string, userPrompt: string, signal?: AbortSignal): Promise<LLMCallResult> {
  const apiKey = getGeminiKey();
  if (!apiKey) throw new Error('Gemini API key not configured.');
  const model = getGeminiModel();

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const { text, inputTokens, outputTokens } = await geminiGenerateText({
    apiKey, model, systemInstruction: systemPrompt, prompt: userPrompt,
    temperature: 0.9, maxOutputTokens: 8192, signal,
  });

  trackUsage({ provider: 'gemini', model, feature: 'lyric-polish', inputTokens, outputTokens });
  return { text, inputTokens, outputTokens, provider: 'gemini', model };
}

async function callClaude(systemPrompt: string, userPrompt: string, signal?: AbortSignal): Promise<LLMCallResult> {
  const apiKey = getClaudeKey();
  if (!apiKey) throw new Error('Claude API key not configured.');
  const model = getClaudeModel();

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const { text, inputTokens, outputTokens } = await anthropicGenerateText({
    apiKey, baseURL: getClaudeProxy(), model,
    systemPrompt, userPrompt, temperature: 0.9, maxTokens: 8192, signal,
  });

  trackUsage({ provider: 'claude', model, feature: 'lyric-polish', inputTokens, outputTokens });
  return { text, inputTokens, outputTokens, provider: 'claude', model };
}

async function callOpenAI(
  systemPrompt: string, userPrompt: string,
  endpoint: string, apiKey: string, model: string,
  signal?: AbortSignal
): Promise<LLMCallResult> {
  if (!apiKey) throw new Error(`API key not configured for ${endpoint.includes('x.ai') ? 'xAI' : 'OpenAI'}.`);
  const providerName = endpoint.includes('x.ai') ? 'xai' : 'openai';

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const response = await openaiCompatFetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({
      model,
      temperature: 0.9,
      max_tokens: 8192,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
    }),
    signal,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: { message?: string } }).error?.message || `${providerName} API error: ${response.status}`);
  }

  const data = await response.json() as {
    choices?: { message?: { content?: string } }[];
    usage?: { prompt_tokens?: number; completion_tokens?: number; cost_in_usd_ticks?: number };
  };

  const text = data.choices?.[0]?.message?.content || '';
  const inputTokens = data.usage?.prompt_tokens || 0;
  const outputTokens = data.usage?.completion_tokens || 0;
  const costTicks = providerName === 'xai' ? data.usage?.cost_in_usd_ticks : undefined;
  const actualCost = typeof costTicks === 'number' ? costTicks / 10_000_000_000 : undefined;

  trackUsage({ provider: providerName, model, feature: 'lyric-polish', inputTokens, outputTokens, actualCost });
  return { text, inputTokens, outputTokens, provider: providerName, model };
}

// =============================================================================
// RESPONSE PARSING
// =============================================================================

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

function extractJSON(text: string): string {
  let cleaned = text.trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '');

  const match = cleaned.match(/\{[\s\S]*\}/);
  if (match) return match[0];

  // Try to recover truncated JSON — find the opening brace and close it
  const braceIdx = cleaned.indexOf('{');
  if (braceIdx >= 0) {
    let truncated = cleaned.substring(braceIdx);
    // Count unclosed braces/brackets and close them
    let braces = 0, brackets = 0, inString = false, escaped = false;
    for (const ch of truncated) {
      if (escaped) { escaped = false; continue; }
      if (ch === '\\') { escaped = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === '{') braces++;
      else if (ch === '}') braces--;
      else if (ch === '[') brackets++;
      else if (ch === ']') brackets--;
    }
    // Remove trailing comma or partial value
    truncated = truncated.replace(/,\s*"[^"]*"?\s*:?\s*[^}\]]*$/, '');
    truncated = truncated.replace(/,\s*$/, '');
    // Close open brackets/braces
    for (let i = 0; i < brackets; i++) truncated += ']';
    for (let i = 0; i < braces; i++) truncated += '}';
    return truncated;
  }

  throw new Error('No JSON object found in response');
}

function normalizeScore(val: unknown): number {
  let num: number;
  if (typeof val === 'number') {
    num = val;
  } else if (typeof val === 'string') {
    num = parseFloat(val);
    if (isNaN(num)) return 5;
  } else {
    return 5;
  }

  if (num > 0 && num < 1) {
    num = Math.round(num * 10);
  }

  return clamp(Math.round(num), 0, 10);
}

const SCORE_KEYS: (keyof LyricDraftScores)[] = [
  'structure', 'hook', 'imagery', 'authenticity', 'emotionalImpact', 'overall',
];

function hasAnyScores(obj: unknown): obj is Record<string, unknown> {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  // Accept if at least 'overall' or any 3 score keys are present
  return o.overall !== undefined || SCORE_KEYS.filter(k => o[k] !== undefined).length >= 3;
}

const VALID_CHANGE_TYPES = new Set(['rewrite', 'tweak', 'keep']);

function parseSuggestion(raw: Record<string, unknown>, originalLyrics?: string): SectionSuggestion {
  const changeType = VALID_CHANGE_TYPES.has(String(raw.changeType))
    ? String(raw.changeType) as 'rewrite' | 'tweak' | 'keep'
    : 'tweak';

  const section = String(raw.section || '');

  // Extract originalText from the original lyrics if not provided by the agent
  let originalText = typeof raw.originalText === 'string' && raw.originalText.trim()
    ? raw.originalText
    : '';

  if (!originalText && originalLyrics && section) {
    // Try to extract the section text from the original lyrics
    const sectionRegex = new RegExp(
      `\\[${section.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]\\s*\\n([\\s\\S]*?)(?=\\n\\[|$)`,
      'i'
    );
    const sectionMatch = originalLyrics.match(sectionRegex);
    if (sectionMatch) {
      originalText = sectionMatch[1].trim();
    }
  }

  return {
    section,
    originalText,
    improvedText: typeof raw.improvedText === 'string' && raw.improvedText.trim()
      ? raw.improvedText
      : originalText, // Fall back to original if empty (for "keep" items)
    rationale: String(raw.rationale || ''),
    changeType,
  };
}

export function parsePolishReviewResponse(text: string, originalLyrics?: string): RawPolishReviewResponse {
  const jsonStr = extractJSON(text);
  let parsed: Record<string, unknown>;

  try {
    parsed = JSON.parse(jsonStr);
  } catch {
    try {
      const repaired = jsonStr.replace(/,\s*([}\]])/g, '$1');
      parsed = JSON.parse(repaired);
    } catch (e2) {
      console.error('🔴 [LyricPolish] Failed to parse review JSON:', text.substring(0, 500));
      throw new Error('Failed to parse review response as JSON');
    }
  }

  if (!hasAnyScores(parsed.scores)) {
    console.error('🔴 [LyricPolish] Invalid scores:', parsed.scores);
    throw new Error('Invalid scores structure in review response');
  }

  const rawScores = parsed.scores as Record<string, unknown>;
  const scores: LyricDraftScores = {
    structure: normalizeScore(rawScores.structure),
    hook: normalizeScore(rawScores.hook),
    imagery: normalizeScore(rawScores.imagery),
    authenticity: normalizeScore(rawScores.authenticity),
    emotionalImpact: normalizeScore(rawScores.emotionalImpact),
    overall: normalizeScore(rawScores.overall),
  };

  const suggestions: SectionSuggestion[] = Array.isArray(parsed.suggestions)
    ? parsed.suggestions.map((s: Record<string, unknown>) => parseSuggestion(s, originalLyrics))
    : [];

  return {
    scores,
    strengths: Array.isArray(parsed.strengths) ? parsed.strengths.map(String) : [],
    weaknesses: Array.isArray(parsed.weaknesses) ? parsed.weaknesses.map(String) : [],
    suggestions,
    overallAssessment: typeof parsed.overallAssessment === 'string' ? parsed.overallAssessment : '',
    confidence: clamp(typeof parsed.confidence === 'number' ? parsed.confidence : 0.5, 0, 1),
  };
}

export function parsePolishSynthesisResponse(text: string): RawPolishSynthesisResponse {
  const jsonStr = extractJSON(text);
  let parsed: Record<string, unknown>;

  try {
    parsed = JSON.parse(jsonStr);
  } catch {
    try {
      const repaired = jsonStr.replace(/,\s*([}\]])/g, '$1');
      parsed = JSON.parse(repaired);
    } catch (e2) {
      console.error('🔴 [LyricPolish] Failed to parse synthesis JSON:', text.substring(0, 500));
      throw new Error('Failed to parse polish synthesis response as JSON');
    }
  }

  const sectionDecisions: SectionDecision[] = Array.isArray(parsed.sectionDecisions)
    ? parsed.sectionDecisions.map((d: Record<string, unknown>) => ({
        section: String(d.section || ''),
        sourceAgentId: String(d.sourceAgentId || ''),
        changeType: VALID_CHANGE_TYPES.has(String(d.changeType))
          ? String(d.changeType) as 'rewrite' | 'tweak' | 'keep'
          : 'tweak',
        rationale: String(d.rationale || ''),
      }))
    : [];

  return {
    polishedLyrics: typeof parsed.polishedLyrics === 'string' ? parsed.polishedLyrics : '',
    title: typeof parsed.title === 'string' ? parsed.title : 'Untitled',
    styleTags: typeof parsed.styleTags === 'string' && parsed.styleTags.trim()
      ? parsed.styleTags.trim().substring(0, 1000)
      : 'Southern Rock, Rock',
    excludedStyles: typeof parsed.excludedStyles === 'string'
      ? parsed.excludedStyles.trim().substring(0, 500)
      : '',
    summary: typeof parsed.summary === 'string' ? parsed.summary : '',
    sectionDecisions,
    overallImprovement: typeof parsed.overallImprovement === 'string' ? parsed.overallImprovement : '',
  };
}

// =============================================================================
// PROMPT BUILDING
// =============================================================================

/**
 * Strip any existing vocal physics blocks from pasted lyrics before review.
 */
function stripPhysicsBlocks(lyrics: string): string {
  return lyrics
    .replace(/\{LAYER-1 VOCAL PHYSICS\}\n(?:\[.*\]\n?)*/g, '')
    .replace(/\{SCREAM-PHYSICS:.*?\}\n(?:\[.*\]\n?)*/g, '')
    .replace(/\{DEEP-DIVE VOCAL LAYERS\}\n(?:(?:\{(?:DIALECT|AGING|BREATH|ARC)-PHYSICS:.*?\}\n\[.*\]\n?)*)/g, '')
    .replace(/\{NARRATIVE-ENGINE\}[\s\S]*?\{\/NARRATIVE-ENGINE\}/g, '')
    .trim();
}

function buildPolishReviewPrompt(
  lyrics: string,
  genre: string,
  moods: string[],
  arch?: LyricArchitectureContext,
): string {
  const moodStr = moods.length > 0 ? moods.join(', ') : 'Dark, Gritty';
  const cleanLyrics = stripPhysicsBlocks(lyrics);

  const vocalArch = arch?.personaOverride?.vocalArchitecture || arch?.vocalArchitecture;
  const prodArch = arch?.personaOverride?.productionArchitecture || arch?.productionArchitecture;

  let prompt = `Review and improve the following lyrics. Grade each dimension 0-10 and provide section-by-section improvement suggestions.

GENRE: ${genre}
MOOD: ${moodStr}

LYRICS TO REVIEW:
${cleanLyrics}`;

  if (vocalArch) {
    prompt += `\n\nVOCAL CHARACTER (consider these when suggesting improvements):\n${vocalArch}`;
  }
  if (prodArch) {
    prompt += `\n\nPRODUCTION ARCHITECTURE:\n${prodArch}`;
  }
  if (arch?.riffDnaBlock) {
    prompt += `\n\nRIFF DNA:\n${arch.riffDnaBlock}`;
  }
  if (arch?.narrativeBlock) {
    prompt += `\n\nNARRATIVE CONTEXT:\n${arch.narrativeBlock}`;
  }

  prompt += `\n\nReview every section of the lyrics. For each section, either suggest specific improvements or mark as "keep" if already strong.
Remember: respond in JSON only.`;

  return prompt;
}

function buildPolishSynthesisPrompt(
  lyrics: string,
  reviews: PolishReview[],
  genre: string,
  moods: string[],
  arch?: LyricArchitectureContext,
): string {
  const moodStr = moods.length > 0 ? moods.join(', ') : 'Dark, Gritty';
  const cleanLyrics = stripPhysicsBlocks(lyrics);

  const vocalArch = arch?.personaOverride?.vocalArchitecture || arch?.vocalArchitecture;
  const prodArch = arch?.personaOverride?.productionArchitecture || arch?.productionArchitecture;

  let prompt = `[POLISH CONTEXT]\n`;
  prompt += `GENRE: ${genre}\n`;
  prompt += `MOOD: ${moodStr}\n`;

  if (vocalArch) {
    prompt += `\nVOCAL CHARACTER:\n${vocalArch}\n`;
  }
  if (prodArch) {
    prompt += `\nPRODUCTION ARCHITECTURE:\n${prodArch}\n`;
  }
  if (arch?.riffDnaBlock) {
    prompt += `\nRIFF DNA:\n${arch.riffDnaBlock}\n`;
  }

  prompt += `\n[ORIGINAL LYRICS]\n${cleanLyrics}\n`;

  prompt += `\n[4 SPECIALIST REVIEWS — MERGE THE BEST IMPROVEMENTS]\n\n`;
  prompt += `Below are reviews from 4 specialist editors, each with section-by-section improvement suggestions. `;
  prompt += `Read all 4 reviews, then apply the best suggestions to create one polished version.\n\n`;

  // Stable ordering by canonical agent list
  const agentOrder = LYRIC_WRITER_AGENTS.map(a => a.id);
  const sortedReviews = [...reviews].sort(
    (a, b) => agentOrder.indexOf(a.agentId) - agentOrder.indexOf(b.agentId)
  );

  for (const review of sortedReviews) {
    prompt += `--- ${review.agentName} [${review.agentId}] (overall: ${review.scores.overall}/10, confidence: ${(review.confidence * 100).toFixed(0)}%) ---\n`;
    prompt += `ASSESSMENT: ${review.overallAssessment}\n`;
    prompt += `STRENGTHS: ${review.strengths.join(', ')}\n`;
    prompt += `WEAKNESSES: ${review.weaknesses.join(', ')}\n`;
    prompt += `SUGGESTIONS:\n`;
    for (const s of review.suggestions) {
      prompt += `  [${s.section}] (${s.changeType}): ${s.rationale}\n`;
      if (s.changeType !== 'keep') {
        prompt += `    Original: ${s.originalText.substring(0, 200)}\n`;
        prompt += `    Improved: ${s.improvedText.substring(0, 200)}\n`;
      }
    }
    prompt += `\n`;
  }

  prompt += `\nMerge the best suggestions into one polished version of the original lyrics. Prefer minimal changes that maximize impact. Attribute every section decision.`;
  prompt += `\nIMPORTANT: Keep stanzas SHORT — 4 lines per verse, 4 lines per chorus. No 8-line blocks. Tight and punchy.`;
  prompt += `\n\nFor styleTags: Generate DETAILED Suno-compatible style tags. Be exhaustive — use the full 1000 character limit.`;
  prompt += `\nFor excludedStyles: Generate specific anti-tags that contradict the song's character.`;

  return prompt;
}

// =============================================================================
// REVIEW EXECUTION
// =============================================================================

/** Execute a single agent review with timeout + abort wiring. */
async function executeSingleReview(
  agent: LyricAgent,
  userPrompt: string,
  cleanLyrics: string,
  config: LyricDeliberationConfig,
  signal?: AbortSignal,
): Promise<PolishReview> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const polishPrompt = POLISH_AGENT_PROMPTS[agent.id];
  if (!polishPrompt) throw new Error(`No polish prompt for agent ${agent.id}`);

  const agentController = new AbortController();
  const timeout = setTimeout(() => agentController.abort(), config.perAgentTimeoutMs);
  const onParentAbort = () => agentController.abort();
  signal?.addEventListener('abort', onParentAbort);

  try {
    const { text, provider, model } = await callProvider(
      polishPrompt, userPrompt, agent.preferredProvider, agentController.signal
    );
    const parsed = parsePolishReviewResponse(text, cleanLyrics);

    return {
      agentId: agent.id,
      agentName: agent.name,
      scores: parsed.scores,
      strengths: parsed.strengths,
      weaknesses: parsed.weaknesses,
      suggestions: parsed.suggestions,
      overallAssessment: parsed.overallAssessment,
      confidence: parsed.confidence,
      provider,
      model,
    };
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', onParentAbort);
  }
}

async function executeReviews(
  agents: LyricAgent[],
  lyrics: string,
  genre: string,
  moods: string[],
  config: LyricDeliberationConfig,
  arch?: LyricArchitectureContext,
  signal?: AbortSignal,
  onProgress?: (event: PolishProgressEvent) => void,
): Promise<{ reviews: PolishReview[]; failedAgents: string[]; failureReasons: Record<string, string> }> {
  const userPrompt = buildPolishReviewPrompt(lyrics, genre, moods, arch);
  const cleanLyrics = stripPhysicsBlocks(lyrics);

  // Stagger agent starts by 300ms to reduce rate-limit pressure
  const promises = agents.map(async (agent, i): Promise<PolishReview> => {
    if (i > 0) await new Promise(r => setTimeout(r, i * 300));
    return executeSingleReview(agent, userPrompt, cleanLyrics, config, signal);
  });

  const results = await Promise.allSettled(promises);
  const reviews: PolishReview[] = [];
  const failedAgentIds: string[] = [];
  const failureReasons: Record<string, string> = {};

  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      reviews.push(result.value);
      onProgress?.({ type: 'reviewer-complete', agentId: agents[i].id });
    } else {
      const agentId = agents[i].id;
      const reason = result.reason instanceof Error ? result.reason.message : 'Unknown error';
      failedAgentIds.push(agentId);
      failureReasons[agentId] = reason;
      console.warn(`⚠️ [LyricPolish] Reviewer ${agentId} failed:`, result.reason);
      onProgress?.({ type: 'reviewer-failed', agentId, error: reason });
    }
  });

  // ── Auto-retry failed agents once (sequentially to avoid rate limits) ──
  if (failedAgentIds.length > 0) {
    console.log(`🔄 [LyricPolish] Retrying ${failedAgentIds.length} failed agent(s): ${failedAgentIds.join(', ')}`);

    for (const agentId of [...failedAgentIds]) {
      if (signal?.aborted) break;

      const agent = agents.find(a => a.id === agentId);
      if (!agent) continue;

      await new Promise(r => setTimeout(r, 1000));

      try {
        const review = await executeSingleReview(agent, userPrompt, cleanLyrics, config, signal);
        reviews.push(review);
        const idx = failedAgentIds.indexOf(agentId);
        if (idx >= 0) failedAgentIds.splice(idx, 1);
        delete failureReasons[agentId];
        console.log(`✅ [LyricPolish] Retry succeeded for ${agentId}`);
        onProgress?.({ type: 'reviewer-complete', agentId });
      } catch (retryErr) {
        const reason = retryErr instanceof Error ? retryErr.message : 'Unknown error';
        failureReasons[agentId] = `Retry failed: ${reason}`;
        console.warn(`⚠️ [LyricPolish] Retry also failed for ${agentId}:`, retryErr);
      }
    }
  }

  return { reviews, failedAgents: failedAgentIds, failureReasons };
}

// =============================================================================
// SYNTHESIS
// =============================================================================

async function synthesizePolish(
  lyrics: string,
  reviews: PolishReview[],
  genre: string,
  moods: string[],
  arch?: LyricArchitectureContext,
  signal?: AbortSignal,
): Promise<PolishSynthesis> {
  const prompt = buildPolishSynthesisPrompt(lyrics, reviews, genre, moods, arch);
  const { text } = await callProvider(
    POLISH_SYNTHESIZER_SYSTEM_PROMPT, prompt, undefined, signal
  );

  const parsed = parsePolishSynthesisResponse(text);

  return {
    polishedLyrics: parsed.polishedLyrics,
    title: parsed.title,
    styleTags: parsed.styleTags.substring(0, 1000).trim() || 'Southern Rock, Rock',
    excludedStyles: parsed.excludedStyles.substring(0, 500).trim(),
    summary: parsed.summary,
    sectionDecisions: parsed.sectionDecisions,
    overallImprovement: parsed.overallImprovement,
  };
}

// =============================================================================
// MAIN ENTRY POINT
// =============================================================================

export async function runLyricPolish(
  lyrics: string,
  genre: string,
  moods: string[],
  arch?: LyricArchitectureContext,
  config?: Partial<LyricDeliberationConfig>,
  signal?: AbortSignal,
  onProgress?: (event: PolishProgressEvent) => void,
): Promise<PolishResult> {
  const fullConfig: LyricDeliberationConfig = { ...DEFAULT_POLISH_DELIBERATION_CONFIG, ...config };
  const agents = LYRIC_WRITER_AGENTS;
  const startTime = Date.now();

  const totalController = new AbortController();
  const totalTimeout = setTimeout(() => totalController.abort(), fullConfig.totalTimeoutMs);
  const onParentAbort = () => totalController.abort();
  signal?.addEventListener('abort', onParentAbort);

  try {
    // ── Reviews ──
    onProgress?.({ type: 'reviewing-start' });

    const { reviews, failedAgents, failureReasons } = await executeReviews(
      agents, lyrics, genre, moods, fullConfig, arch, totalController.signal, onProgress
    );

    onProgress?.({ type: 'reviewing-complete', successCount: reviews.length });

    if (reviews.length < fullConfig.minAgentsRequired) {
      const reasonDetails = Object.entries(failureReasons)
        .map(([id, reason]) => `${id}: ${reason}`)
        .join('; ');
      throw new Error(
        `Only ${reviews.length} of ${agents.length} reviewers completed. ` +
        `Minimum ${fullConfig.minAgentsRequired} required. ` +
        `Failed: ${reasonDetails || failedAgents.join(', ')}`
      );
    }

    // ── Polish Synthesis ──
    onProgress?.({ type: 'polish-synthesis-start' });

    const synthesis = await synthesizePolish(lyrics, reviews, genre, moods, arch, totalController.signal);

    onProgress?.({ type: 'polish-synthesis-complete' });

    return {
      id: `polish_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
      originalLyrics: lyrics,
      reviews,
      synthesis,
      totalDurationMs: Date.now() - startTime,
      agentCount: agents.length,
      failedAgents,
    };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    onProgress?.({ type: 'error', message: err instanceof Error ? err.message : 'Lyric polish failed' });
    throw err;
  } finally {
    clearTimeout(totalTimeout);
    signal?.removeEventListener('abort', onParentAbort);
  }
}

// =============================================================================
// BRIDGE — Convert polish result to ParsedSunoOutput
// =============================================================================

export function toPolishOutput(
  result: PolishResult,
  formData: HookhouseFormData,
  vocalArchitecture?: string,
): ParsedSunoOutput {
  const s = result.synthesis;
  const rawLyrics = s.polishedLyrics || '';
  const styleTags = (s.styleTags && s.styleTags.trim()) ? s.styleTags.trim().substring(0, 1000) : 'Southern Rock, Rock';
  const excludedStyles = (s.excludedStyles && s.excludedStyles.trim()) ? s.excludedStyles.trim().substring(0, 500) : '';
  const title = s.title || 'Untitled';

  // Extract vocal physics blocks — same pattern as toLyricOutput
  let physicsBlock = '';
  if (vocalArchitecture) {
    const sections: string[] = [];

    const layer1 = vocalArchitecture.match(/\{LAYER-1 VOCAL PHYSICS\}\n(?:\[.*\]\n?)*/);
    if (layer1) sections.push(layer1[0].trim());

    const scream = vocalArchitecture.match(/\{SCREAM-PHYSICS:.*\}\n(?:\[.*\]\n?)*/);
    if (scream) sections.push(scream[0].trim());

    const ddBlocks = [...vocalArchitecture.matchAll(
      /(\{(?:DIALECT|AGING|BREATH|ARC)-PHYSICS:.*\})\n(\[.*\])/g
    )];
    if (ddBlocks.length > 0) {
      sections.push(
        '{DEEP-DIVE VOCAL LAYERS}\n' +
        ddBlocks.map(m => `${m[1]}\n${m[2]}`).join('\n')
      );
    }

    physicsBlock = sections.join('\n\n');
  }
  const lyrics = physicsBlock
    ? `${physicsBlock}\n\n${rawLyrics}`
    : rawLyrics;

  const lyricsAlone = rawLyrics
    .replace(/\[.*?\]\n?/g, '')
    .trim();

  // Build console injection code
  const machineStyle = styleTags.substring(0, 1000).trim();
  const machineLyrics = lyrics.substring(0, 5000).trim();
  const weirdness = formData.weirdness ?? 50;
  const styleStrength = formData.styleStrength ?? 50;

  const javascriptCode = [
    '// --- Hookhouse Pro (Parliament Polish) Terminal ---',
    '// Paste this while on suno.com/create in "Custom Mode"',
    '',
    'function setNativeValue(element, value) {',
    '    if (!element) return;',
    '    const valueSetter = Object.getOwnPropertyDescriptor(element.constructor.prototype, "value")?.set;',
    '    const prototypeValueSetter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(element), "value")?.set;',
    '    if (valueSetter && valueSetter !== prototypeValueSetter) {',
    '        valueSetter.call(element, value);',
    '    } else if (valueSetter) {',
    '        valueSetter.call(element, value);',
    '    }',
    '    element.dispatchEvent(new Event("input", { bubbles: true }));',
    '}',
    '',
    ...SUNO_LYRICS_INJECTION_LINES,
    '',
    'function adjustSlider(sliderLabel, targetValue) {',
    '    const slider = document.querySelector(`div[role="slider"][aria-label*="${sliderLabel}"]`);',
    '    if (slider) {',
    '        slider.focus();',
    '        const pressKey = (key) => slider.dispatchEvent(new KeyboardEvent("keydown", { key, code: key, bubbles: true, cancelable: true }));',
    '        const intervalId = setInterval(() => {',
    '            const currentVal = parseFloat(slider.getAttribute("aria-valuenow"));',
    '            if (Math.abs(currentVal - targetValue) < 1) { clearInterval(intervalId); return; }',
    '            if (currentVal > targetValue) pressKey("ArrowLeft"); else if (currentVal < targetValue) pressKey("ArrowRight");',
    '        }, 10);',
    '    }',
    '}',
    '',
    'console.log("\\uD83D\\uDE80 Injecting Parliament Polish Architecture...");',
    '',
    'injectLyrics(' + JSON.stringify(machineLyrics) + ');',
    '',
    'const styleInput = document.querySelector(\'input[placeholder*="style of music"]\');',
    'if (styleInput) { setNativeValue(styleInput, ' + JSON.stringify(machineStyle) + '); console.log("\\u2713 Style tags injected"); }',
    '',
    'const songTitleInput = document.querySelector(\'input[placeholder*="Song Title"]\');',
    'if (songTitleInput) { setNativeValue(songTitleInput, ' + JSON.stringify(title) + '); console.log("\\u2713 Title injected"); }',
    '',
    'adjustSlider("Weirdness", ' + weirdness + ');',
    'adjustSlider("Style Influence", ' + styleStrength + ');',
    '',
    'console.log("%c Parliament Polish Injection Complete! ", "background: rgb(var(--color-accent)); color: #000; font-size: 14px; font-weight: bold;");',
  ].join('\n');

  const styleLen = styleTags.length;
  const excludeLen = excludedStyles.length;
  const lyricsLen = lyrics.length;

  return {
    titleArtist: `${title} — Parliament Polishers`,
    summary: s.summary,
    styleBlueprint: styleTags,
    style: styleTags,
    excludedStyle: excludedStyles,
    title,
    artist: 'Parliament Polishers',
    lyricsWithTags: lyrics,
    lyricsAlone,
    javascriptCode,
    fullResponse: JSON.stringify(result, null, 2),
    styleStrength,
    weirdness,
    llmModel: result.reviews[0]?.model || 'unknown',
    llmProvider: result.reviews[0]?.provider || 'unknown',
    charLimits: {
      styleTags: { count: styleLen, max: 1000, over: styleLen > 1000 },
      excludedStyles: { count: excludeLen, max: 500, over: excludeLen > 500 },
      lyrics: { count: lyricsLen, max: 5000, over: lyricsLen > 5000 },
    },
  };
}
